import { useInfiniteQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { api } from "../api";
import { clipSearch, searchParams, when } from "../accounts";
import { roleLabel } from "../nav";
import { errorText, unwrap } from "../problem";

// Every page is audited (`user.search`), so the list loads only when asked
// and is never refetched by itself.
export function UsersPage() {
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState<{ q: string; run: number } | null>(null);
  const list = useInfiniteQuery({
    queryKey: ["users", search],
    enabled: search !== null,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET("/users", {
          params: { query: { ...searchParams(search!.q), ...(pageParam ? { cursor: pageParam } : {}) } },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    // Nothing kept once the page closes, so the same search again is fetched (and audited) afresh.
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  function apply(event: FormEvent) {
    event.preventDefault();
    setSearch((previous) => ({ q: draft.trim(), run: (previous?.run ?? 0) + 1 }));
  }

  const users = list.data?.pages.flatMap((page) => page.users) ?? [];
  const matched = list.data?.pages[0]?.matched;
  return (
    <>
      <h1>Users</h1>
      <p className="muted">
        Account metadata only: never locations, places, messages or contacts. Searches and opened accounts are recorded
        in the audit log.
      </p>
      <form className="filters" onSubmit={apply}>
        <label className="grow">
          Search
          <input
            type="search"
            value={draft}
            placeholder="Email, name or account id (blank: newest accounts)"
            onChange={(event) => setDraft(clipSearch(event.target.value))}
          />
        </label>
        <button type="submit">Search</button>
      </form>
      {list.isFetching && !list.isFetchingNextPage && <p>Loading…</p>}
      {list.isError && <p className="error">{errorText(list.error)}</p>}
      {matched !== undefined && (
        <p className="muted">
          {matched.toLocaleString()} {matched === 1 ? "account" : "accounts"}
        </p>
      )}
      {users.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Created</th>
              <th className="num">Circles</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <Link to={`/users/${user.id}`}>{user.name}</Link>
                </td>
                <td>{user.email}</td>
                <td>{when(user.created_at)}</td>
                <td className="num">{user.circles}</td>
                <td>
                  {[user.suspended_at ? "Suspended" : null, user.staff_role ? roleLabel(user.staff_role) : null]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {list.hasNextPage && (
        <button
          type="button"
          className="button-quiet"
          onClick={() => void list.fetchNextPage()}
          disabled={list.isFetchingNextPage}
        >
          {list.isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}
    </>
  );
}
